import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Keyboard from '../../Keyboard';
import RiddledInGuessCells from '../riddledIn/RiddledInGuessCells';
import DailyRiddleHint from '../daily/DailyRiddleHint';
import CustomServerGrid from './CustomServerGrid';
import CustomGameModal from './CustomGameModal';
import CustomOpenEndedGuessWord from './CustomOpenEndedGuessWord';
import { getZoomTransform } from '../../../utils/zoomPosition';
import {
  getCustomGameBySlug,
  startCustomGameRun,
  submitCustomGameGuess,
} from '../../../utils/customGameApi';
import { submitGameIssueReport } from '../../../utils/gameIssueReportApi';
import {
  CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
  CUSTOM_IMAGE_HINT_DISPLAY_MS,
  CUSTOM_IMAGE_HINT_FADE_MS,
  CUSTOM_FULLY_ZOOMED_HINT_TEXT,
  CUSTOM_IMAGE_READY_HINT_TEXT,
  CUSTOM_KEEP_GUESSING_HINT_TEXT,
  CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
  CUSTOM_ZOOM_HINT_DISPLAY_MS,
  CUSTOM_DEFAULT_ZOOM_ORIGIN,
  CUSTOM_STYLE_GRID_5,
  getCustomZoomInterpolationBounds,
  getCustomProgressiveZoomLevel,
} from '../../../constants/games/zoomedInCustomGame';
import './ZoomedInCustomPlayPage.css';

const STATUS_RANK = Object.freeze({
  absent: 1,
  present: 2,
  correct: 3,
});

const toLetterStatusMapFromGuess = ({ guessWord, letterStatuses, previous }) => {
  const next = { ...(previous || {}) };
  const letters = String(guessWord || '').toUpperCase().split('');
  const statuses = Array.isArray(letterStatuses) ? letterStatuses : [];

  for (let i = 0; i < letters.length; i += 1) {
    const letter = letters[i];
    const nextStatus = String(statuses[i] || '').toLowerCase();
    if (!letter || !STATUS_RANK[nextStatus]) continue;
    const previousStatus = next[letter] || '';
    const previousRank = STATUS_RANK[previousStatus] || 0;
    if (STATUS_RANK[nextStatus] > previousRank) {
      next[letter] = nextStatus;
    }
  }

  return next;
};

const buildUsedLettersSet = (statusMap) => {
  const entries = Object.entries(statusMap || {});
  const used = new Set();
  entries.forEach(([letter, status]) => {
    used.add(`${letter}-${status}`);
  });
  return used;
};

const toOptionalNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const ZoomedInCustomPlayPage = ({ mobileSideBySide = false }) => {
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();
  const slug = String(slugParam || '').trim().toLowerCase();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [run, setRun] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revealedWord, setRevealedWord] = useState('');
  const [usedLetterStatusMap, setUsedLetterStatusMap] = useState({});
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalAutoShown, setResultModalAutoShown] = useState(false);
  const [imageHint, setImageHint] = useState({ visible: false, fading: false });
  const [fullZoomHint, setFullZoomHint] = useState({ visible: false, fading: false });
  const inputRef = useRef(null);
  const gridWrapRef = useRef(null);
  const imageHintTimersRef = useRef({ fade: null, clear: null });
  const fullZoomHintTimersRef = useRef({ fade: null, clear: null });
  const shownFullZoomHintRef = useRef(false);
  const [gridWrapHeight, setGridWrapHeight] = useState(0);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportStatus, setReportStatus] = useState({ type: '', text: '' });

  const wordLength = Number(run?.wordLength ?? metadata?.wordLength ?? 0);
  const maxGuesses = toOptionalNumber(run?.maxGuesses ?? metadata?.maxGuesses);
  const clueRevealRow = Number(run?.clueRevealRow ?? metadata?.clueRevealRow ?? 1);
  const fullZoomOutRow = Math.max(
    CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
    Math.floor(
      Number(
        run?.fullZoomOutRow ?? metadata?.fullZoomOutRow ?? CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
      ) || CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
    ),
  );
  const clueSourceText = String(run?.clueText || metadata?.clueText || '').trim();
  const guessesUsed = Math.max(Number(run?.guessesUsed ?? 0) || 0, guesses.length);
  const status = String(run?.status || 'active');
  const isGridStyle = String(run?.style || metadata?.style || '') === CUSTOM_STYLE_GRID_5;
  const isRunActive = Boolean(run?.runId) && status === 'active';
  const isGuessComplete = String(currentGuess || '').trim().length === wordLength;
  const usedLetters = useMemo(
    () => buildUsedLettersSet(usedLetterStatusMap),
    [usedLetterStatusMap],
  );
  const zoomOrigin = useMemo(
    () => ({
      x: toOptionalNumber(run?.zoomOriginX ?? metadata?.zoomOriginX) ?? CUSTOM_DEFAULT_ZOOM_ORIGIN.x,
      y: toOptionalNumber(run?.zoomOriginY ?? metadata?.zoomOriginY) ?? CUSTOM_DEFAULT_ZOOM_ORIGIN.y,
    }),
    [metadata?.zoomOriginX, metadata?.zoomOriginY, run?.zoomOriginX, run?.zoomOriginY],
  );
  const zoomRadius = toOptionalNumber(run?.zoomRadius ?? metadata?.zoomRadius) ?? 0.35;
  const zoomBounds = useMemo(
    () => getCustomZoomInterpolationBounds(zoomRadius),
    [zoomRadius],
  );
  const currentZoomLevel = useMemo(
    () =>
      getCustomProgressiveZoomLevel({
        zoomRadius,
        guessesUsed,
        fullZoomOutRow,
      }),
    [fullZoomOutRow, zoomRadius, guessesUsed],
  );
  const imageTransform = useMemo(
    () => getZoomTransform(zoomOrigin, currentZoomLevel, zoomBounds),
    [currentZoomLevel, zoomBounds, zoomOrigin],
  );

  const canShowClue = Boolean(clueSourceText) && guessesUsed >= clueRevealRow;
  const isTerminalStatus = status === 'failed' || status === 'revealed' || status === 'solved';
  const shouldShowAnswer =
    isTerminalStatus && Boolean(revealedWord);
  const isResultModalVisible = showResultModal && shouldShowAnswer;
  const showStatusPanel = canShowClue || (shouldShowAnswer && !isResultModalVisible);
  const isOpenEndedTypingEnabled = !isGridStyle && isRunActive && !submitting && !isResultModalVisible;
  const isFullyZoomedOut = guessesUsed >= Math.max(1, fullZoomOutRow - 1);
  const gridGuessRows = isGridStyle ? Math.max(1, Number(maxGuesses) || 6) : 0;
  const gridColumns = isGridStyle ? Math.max(1, Number(wordLength) || 5) : 0;
  const shareZoomLevel = zoomBounds.initialZoom;
  const topClassName = [
    'custom-play-page__top',
    isGridStyle ? 'custom-play-page__top--grid' : 'custom-play-page__top--open-ended',
    isGridStyle && mobileSideBySide ? 'custom-play-page__top--grid-mobile-split' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const clearImageHintTimers = useCallback(() => {
    if (imageHintTimersRef.current.fade) {
      window.clearTimeout(imageHintTimersRef.current.fade);
      imageHintTimersRef.current.fade = null;
    }
    if (imageHintTimersRef.current.clear) {
      window.clearTimeout(imageHintTimersRef.current.clear);
      imageHintTimersRef.current.clear = null;
    }
  }, []);

  const hideImageHint = useCallback(() => {
    clearImageHintTimers();
    setImageHint({ visible: false, fading: false });
  }, [clearImageHintTimers]);

  const showImageHint = useCallback(() => {
    clearImageHintTimers();
    setImageHint({ visible: true, fading: false });
    imageHintTimersRef.current.fade = window.setTimeout(() => {
      setImageHint((previous) => (
        previous.visible ? { ...previous, fading: true } : previous
      ));
    }, CUSTOM_IMAGE_HINT_DISPLAY_MS);
    imageHintTimersRef.current.clear = window.setTimeout(() => {
      setImageHint({ visible: false, fading: false });
      imageHintTimersRef.current.fade = null;
      imageHintTimersRef.current.clear = null;
    }, CUSTOM_IMAGE_HINT_DISPLAY_MS + CUSTOM_IMAGE_HINT_FADE_MS);
  }, [clearImageHintTimers]);

  const clearFullZoomHintTimers = useCallback(() => {
    if (fullZoomHintTimersRef.current.fade) {
      window.clearTimeout(fullZoomHintTimersRef.current.fade);
      fullZoomHintTimersRef.current.fade = null;
    }
    if (fullZoomHintTimersRef.current.clear) {
      window.clearTimeout(fullZoomHintTimersRef.current.clear);
      fullZoomHintTimersRef.current.clear = null;
    }
  }, []);

  const hideFullZoomHint = useCallback(() => {
    clearFullZoomHintTimers();
    setFullZoomHint({ visible: false, fading: false });
  }, [clearFullZoomHintTimers]);

  const showFullZoomHint = useCallback(() => {
    hideImageHint();
    clearFullZoomHintTimers();
    setFullZoomHint({ visible: true, fading: false });
    fullZoomHintTimersRef.current.fade = window.setTimeout(() => {
      setFullZoomHint((previous) => (
        previous.visible ? { ...previous, fading: true } : previous
      ));
    }, CUSTOM_ZOOM_HINT_DISPLAY_MS);
    fullZoomHintTimersRef.current.clear = window.setTimeout(() => {
      setFullZoomHint({ visible: false, fading: false });
      fullZoomHintTimersRef.current.fade = null;
      fullZoomHintTimersRef.current.clear = null;
    }, CUSTOM_ZOOM_HINT_DISPLAY_MS + CUSTOM_IMAGE_HINT_FADE_MS);
  }, [clearFullZoomHintTimers, hideImageHint]);

  const hydrateRun = useCallback(async () => {
    if (!slug) {
      setError('Missing custom game slug.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setGuesses([]);
    setCurrentGuess('');
    setRevealedWord('');
    setUsedLetterStatusMap({});
    setShowResultModal(false);
    setResultModalAutoShown(false);
    hideImageHint();
    hideFullZoomHint();
    shownFullZoomHintRef.current = false;

    try {
      const gameMetadata = await getCustomGameBySlug({ slug });
      const startedRun = await startCustomGameRun({ slug });
      const resolvedStyle = startedRun.style ?? gameMetadata.style;
      setMetadata(gameMetadata);
      setRun({
        ...startedRun,
        style: resolvedStyle,
        maxGuesses: startedRun.maxGuesses ?? gameMetadata.maxGuesses,
        clueRevealRow: startedRun.clueRevealRow ?? gameMetadata.clueRevealRow,
        wordLength: startedRun.wordLength ?? gameMetadata.wordLength,
        clueText: startedRun.clueText ?? gameMetadata.clueText ?? '',
        fullZoomOutRow:
          startedRun.fullZoomOutRow ??
          gameMetadata.fullZoomOutRow ??
          CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
        zoomOriginX: startedRun.zoomOriginX ?? gameMetadata.zoomOriginX,
        zoomOriginY: startedRun.zoomOriginY ?? gameMetadata.zoomOriginY,
        zoomRadius: startedRun.zoomRadius ?? gameMetadata.zoomRadius,
      });
      setLoading(false);
      if (resolvedStyle !== CUSTOM_STYLE_GRID_5) {
        setTimeout(() => inputRef.current?.focus?.(), 40);
      }
    } catch (apiError) {
      setError(apiError?.message || 'Failed to load custom game.');
      setLoading(false);
    }
  }, [hideFullZoomHint, hideImageHint, slug]);

  useEffect(() => {
    hydrateRun();
  }, [hydrateRun]);

  useEffect(() => {
    hideImageHint();
    hideFullZoomHint();
    shownFullZoomHintRef.current = false;
  }, [hideFullZoomHint, hideImageHint, metadata?.imageUrl]);

  useEffect(() => {
    if (!isFullyZoomedOut) return;
    if (isTerminalStatus || isResultModalVisible) return;
    if (shownFullZoomHintRef.current) return;
    shownFullZoomHintRef.current = true;
    showFullZoomHint();
  }, [isFullyZoomedOut, isResultModalVisible, isTerminalStatus, showFullZoomHint]);

  useEffect(() => {
    if (!isOpenEndedTypingEnabled || loading) return undefined;
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [guesses.length, isOpenEndedTypingEnabled, loading, run?.runId]);

  const submitInappropriateContentReport = useCallback(async () => {
    if (!slug || reportSubmitting) return;

    setReportStatus({ type: '', text: '' });
    setReportSubmitting(true);
    try {
      await submitGameIssueReport({
        gameType: 'custom',
        reason: 'inappropriate_content',
        note: `Custom game report. slug=${slug}`,
        imageUrl: metadata?.imageUrl || '',
      });
      setReportStatus({
        type: 'success',
        text: 'Thanks. Your report was submitted for review.',
      });
    } catch (apiError) {
      const message =
        apiError?.userMessage ||
        apiError?.message ||
        'Could not submit report right now. Please try again.';
      setReportStatus({ type: 'error', text: message });
    } finally {
      setReportSubmitting(false);
    }
  }, [metadata?.imageUrl, reportSubmitting, slug]);

  const submitGuess = useCallback(async () => {
    if (!isRunActive || submitting) return;
    if (!isGuessComplete) {
      setError(`Please enter exactly ${wordLength} letters.`);
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const response = await submitCustomGameGuess({
        runId: run.runId,
        guess: currentGuess,
      });

      const committedWord = String(currentGuess || '').toUpperCase();
      setGuesses((previous) => [
        ...previous,
        {
          word: committedWord,
          percentage: response.percentage,
          isCorrect: response.isCorrect,
          letterStatuses: response.letterStatuses || null,
        },
      ]);

      if (Array.isArray(response.letterStatuses) && response.letterStatuses.length > 0) {
        setUsedLetterStatusMap((previous) =>
          toLetterStatusMapFromGuess({
            guessWord: committedWord,
            letterStatuses: response.letterStatuses,
            previous,
          }),
        );
      }

      setRun((previous) => ({
        ...(previous || {}),
        guessesUsed: response.guessesUsed,
        status: response.status,
        clueText: response.clueText || previous?.clueText || '',
        fullZoomOutRow: response.fullZoomOutRow ?? previous?.fullZoomOutRow,
      }));

      setCurrentGuess('');
      if (response.revealWord) {
        setRevealedWord(String(response.revealWord).toUpperCase());
      } else if (response.isCorrect) {
        setRevealedWord(committedWord);
      }
    } catch (apiError) {
      setError(apiError?.message || 'Failed to submit guess.');
    } finally {
      setSubmitting(false);
    }
  }, [currentGuess, isGuessComplete, isRunActive, run?.runId, submitting, wordLength]);

  const handleGridKeyPress = useCallback(
    (key) => {
      if (!isGridStyle || !isRunActive || submitting || isResultModalVisible) return;

      if (key === 'ENTER') {
        submitGuess();
        return;
      }
      if (key === '⌫' || key === 'Backspace') {
        setCurrentGuess((previous) => previous.slice(0, -1));
        return;
      }
      if (!/^[A-Z]$/.test(key)) return;
      setCurrentGuess((previous) => {
        if (previous.length >= wordLength) return previous;
        return `${previous}${key}`;
      });
    },
    [isGridStyle, isResultModalVisible, isRunActive, submitting, submitGuess, wordLength],
  );

  useEffect(() => {
    if (!isGridStyle) return undefined;
    const onKeyDown = (event) => {
      if (!isRunActive || isResultModalVisible) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        handleGridKeyPress('ENTER');
        return;
      }
      if (event.key === 'Backspace') {
        handleGridKeyPress('Backspace');
        return;
      }
      const letter = String(event.key || '').toUpperCase();
      if (/^[A-Z]$/.test(letter)) {
        handleGridKeyPress(letter);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleGridKeyPress, isGridStyle, isResultModalVisible, isRunActive]);

  useEffect(() => {
    if (!isGridStyle) {
      setGridWrapHeight(0);
      return undefined;
    }

    const element = gridWrapRef.current;
    if (!element) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.round(element.getBoundingClientRect().height);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
      setGridWrapHeight((previous) => (Math.abs(previous - nextHeight) <= 1 ? previous : nextHeight));
    };

    updateHeight();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        updateHeight();
      });
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [gridGuessRows, isGridStyle, mobileSideBySide, wordLength]);

  useEffect(() => {
    if (!shouldShowAnswer || !isTerminalStatus || resultModalAutoShown) return;
    setShowResultModal(true);
    setResultModalAutoShown(true);
  }, [isTerminalStatus, resultModalAutoShown, shouldShowAnswer]);

  useEffect(() => () => {
    clearImageHintTimers();
  }, [clearImageHintTimers]);

  useEffect(() => () => {
    clearFullZoomHintTimers();
  }, [clearFullZoomHintTimers]);

  if (loading) {
    return (
      <div className="game custom-play-page">
        <div className="custom-play-page__loading">Loading custom game...</div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="game custom-play-page">
        <div className="custom-play-page__error">{error || 'Custom game not found.'}</div>
      </div>
    );
  }

  return (
    <div
      className={`game custom-play-page${
        isGridStyle && mobileSideBySide ? ' custom-play-page--mobile-split' : ''
      }`}
      style={
        isGridStyle
          ? {
              '--custom-guess-rows': String(gridGuessRows),
              '--custom-grid-cols': String(gridColumns),
            }
          : undefined
      }
    >
      <div className={topClassName}>
        <div
          className="custom-play-page__image-wrap"
          style={isGridStyle && gridWrapHeight > 0 ? { height: `${gridWrapHeight}px` } : undefined}
        >
          <img
            src={metadata.imageUrl}
            alt="Custom puzzle"
            className="custom-play-page__image"
            onContextMenu={(event) => {
              event.preventDefault();
            }}
            onDragStart={(event) => {
              event.preventDefault();
            }}
            draggable={false}
            style={{
              width: `${currentZoomLevel}%`,
              height: `${currentZoomLevel}%`,
              transform: imageTransform,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
            onLoad={showImageHint}
            onError={hideImageHint}
          />
          {imageHint.visible ? (
            <div
              className="custom-play-page__image-hint-stack"
              role="status"
              aria-live="polite"
            >
              <div
                className={`custom-play-page__image-hint ${
                  imageHint.fading ? 'custom-play-page__image-hint--fade' : ''
                }`}
              >
                {CUSTOM_IMAGE_READY_HINT_TEXT}
              </div>
              <div
                className={`custom-play-page__image-hint ${
                  imageHint.fading ? 'custom-play-page__image-hint--fade' : ''
                }`}
              >
                {CUSTOM_KEEP_GUESSING_HINT_TEXT}
              </div>
            </div>
          ) : null}
          {fullZoomHint.visible ? (
            <div
              className="custom-play-page__image-hint-stack"
              role="status"
              aria-live="polite"
            >
              <div
                className={`custom-play-page__image-hint ${
                  fullZoomHint.fading ? 'custom-play-page__image-hint--fade' : ''
                }`}
              >
                {CUSTOM_FULLY_ZOOMED_HINT_TEXT}
              </div>
            </div>
          ) : null}
        </div>

        {isGridStyle ? (
          <div ref={gridWrapRef} className="custom-play-page__grid-wrap">
            <CustomServerGrid
              // iOS Safari can keep a stale board width when split/stack toggles.
              // Remounting forces a fresh layout calculation (same fix as Unlimited/Generations).
              key={`custom-${slug || 'game'}-${mobileSideBySide ? 'split' : 'stack'}-${gridGuessRows}`}
              maxGuesses={maxGuesses ?? 6}
              wordLength={wordLength}
              guesses={guesses}
              currentGuess={currentGuess}
              currentRow={guesses.length}
              isTypingEnabled={isRunActive && !isResultModalVisible}
            />
          </div>
        ) : null}
      </div>

      {showStatusPanel ? (
        <div className="custom-play-page__status">
          <DailyRiddleHint text={canShowClue ? clueSourceText : ''} />
          {shouldShowAnswer ? (
            <div className="custom-play-page__answer">
              Answer: <strong>{revealedWord}</strong>
            </div>
          ) : null}
        </div>
      ) : null}

      {isGridStyle ? (
        <div className="game-bottom-container custom-play-page__keyboard-wrap">
          <Keyboard
            onKeyPress={handleGridKeyPress}
            usedLetters={usedLetters}
            disabled={!isRunActive || submitting || isResultModalVisible}
            isSubmitting={submitting}
          />
        </div>
      ) : (
        <div className="custom-play-page__open-ended">
          <div className="custom-play-page__open-ended-input">
            <RiddledInGuessCells
              ref={inputRef}
              value={currentGuess}
              onChange={(next) => setCurrentGuess(next)}
              onSubmit={submitGuess}
              maxLength={wordLength}
              disabled={!isOpenEndedTypingEnabled}
              keepFocused={isOpenEndedTypingEnabled}
              autoFocus={isOpenEndedTypingEnabled}
              compact
              className="custom-play-page__guess-cells"
            />
            <button
              type="button"
              className="custom-play-page__submit"
              onClick={submitGuess}
              disabled={!isRunActive || !isGuessComplete || submitting || isResultModalVisible}
            >
              {submitting ? 'Submitting...' : 'Guess'}
            </button>
          </div>

          {guesses.length > 0 ? (
            <div className="custom-play-page__guess-history">
              {guesses.map((entry, index) => (
                <div key={`${entry.word}-${index}`} className="custom-play-page__guess-row">
                  <CustomOpenEndedGuessWord
                    className="custom-play-page__guess-word"
                    word={entry.word}
                    letterStatuses={entry.letterStatuses}
                  />
                  <div className="custom-play-page__guess-bar">
                    <div
                      className="custom-play-page__guess-bar-fill"
                      style={{ width: `${Math.max(0, Math.min(100, Number(entry.percentage) || 0))}%` }}
                    />
                  </div>
                  <span className="custom-play-page__guess-pct">{entry.percentage}%</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {error ? <div className="custom-play-page__error">{error}</div> : null}
      <div className="custom-play-page__report">
        <button
          type="button"
          className="custom-play-page__report-button"
          onClick={submitInappropriateContentReport}
          disabled={reportSubmitting}
        >
          {reportSubmitting ? 'Submitting report...' : 'Report custom game'}
        </button>
        {reportStatus.text ? (
          <div
            className={`custom-play-page__report-note ${
              reportStatus.type === 'error'
                ? 'custom-play-page__report-note--error'
                : 'custom-play-page__report-note--success'
            }`}
          >
            {reportStatus.text}
          </div>
        ) : null}
      </div>

      {isResultModalVisible ? (
        <CustomGameModal
          status={status}
          isGridStyle={isGridStyle}
          imageUrl={metadata.imageUrl}
          solution={revealedWord}
          guesses={guesses}
          maxGuesses={maxGuesses}
          shareZoomLevel={shareZoomLevel}
          shareZoomOrigin={zoomOrigin}
          gameSlug={slug}
          onClose={() => setShowResultModal(false)}
          onHome={() => navigate('/')}
        />
      ) : null}
    </div>
  );
};

export default ZoomedInCustomPlayPage;
